import { useMemo, useState } from "react";
import { Check, Search, Trophy, X } from "lucide-react";

export function CreativePicker({ contractors, selectedIds, onChange }) {
  const [q, setQ] = useState("");
  const [exp, setExp] = useState("");
  const [starOnly, setStarOnly] = useState(false);

  const expertiseOptions = useMemo(() => {
    const set = new Set();
    contractors.forEach((c) => (c.expertise || []).forEach((e) => set.add(e)));
    return [...set].sort();
  }, [contractors]);

  const toggle = (id) => onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return contractors.filter((c) => {
      if (starOnly && !c.all_star) return false;
      if (exp && !(c.expertise || []).includes(exp)) return false;
      if (ql && !`${c.name} ${c.email}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [contractors, q, exp, starOnly]);

  const selected = contractors.filter((c) => selectedIds.includes(c.id));

  return (
    <div data-testid="creative-picker" className="border border-[#2A2A2A] bg-[#050505]">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 border-b border-[#1A1A1A]">
          {selected.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 text-xs bg-[#FF4D0020] border border-[#FF4D00] text-[#FF4D00] px-2 py-1">
              {c.name}
              <button type="button" data-testid={`picker-remove-${c.id}`} onClick={() => toggle(c.id)} className="hover:text-white"><X className="w-3 h-3" /></button>
            </span>
          ))}
          <button type="button" data-testid="picker-clear" onClick={() => onChange([])} className="text-[11px] uppercase tracking-wide text-[#A1A1AA] hover:text-white px-1">Clear all</button>
        </div>
      )}

      <div className="p-2 space-y-2 border-b border-[#1A1A1A]">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-[#666] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input data-testid="picker-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="w-full bg-[#0A0A0A] border border-[#2A2A2A] pl-8 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF4D00]" />
        </div>
        <div className="flex flex-wrap gap-1.5 items-center">
          <select data-testid="picker-expertise" value={exp} onChange={(e) => setExp(e.target.value)} className="bg-[#0A0A0A] border border-[#2A2A2A] px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#FF4D00]">
            <option value="">All expertise</option>
            {expertiseOptions.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <button type="button" data-testid="picker-allstar" onClick={() => setStarOnly((v) => !v)}
            className={`inline-flex items-center gap-1 text-xs px-2 py-1.5 border transition-colors ${starOnly ? "border-[#FF4D00] bg-[#FF4D0020] text-[#FF4D00]" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#FF4D00]"}`}>
            <Trophy className="w-3.5 h-3.5" /> All-Stars
          </button>
          <span className="text-[10px] text-[#666] ml-auto">{filtered.length} shown · {selected.length} selected</span>
        </div>
      </div>

      <div className="max-h-[260px] overflow-y-auto divide-y divide-[#1A1A1A]">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-xs text-[#555]">
            {contractors.length === 0 ? "No creatives with login accounts yet. Send logins to Fully-Approved creatives first." : "No creatives match these filters."}
          </div>
        ) : filtered.map((c) => {
          const on = selectedIds.includes(c.id);
          return (
            <button type="button" key={c.id} data-testid={`picker-row-${c.id}`} onClick={() => toggle(c.id)}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors ${on ? "bg-[#FF4D0015]" : "hover:bg-[#111111]"}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm truncate ${on ? "text-white" : "text-[#E4E4E7]"}`}>{c.name}</span>
                  {c.role === "admin" && <span className="text-[9px] uppercase tracking-wide text-[#FF4D00]">Admin</span>}
                  {c.all_star && <Trophy className="w-3 h-3 text-[#FF4D00]" title="All-Star" />}
                </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  {(c.expertise || []).slice(0, 4).map((e) => (
                    <span key={e} className="text-[10px] uppercase tracking-wide text-[#A1A1AA] border border-[#2A2A2A] px-1.5 py-0.5">{e}</span>
                  ))}
                  {(c.expertise || []).length === 0 && <span className="text-[10px] text-[#555]">No expertise on file</span>}
                  <span className="text-[10px] text-[#666]">· {c.completed_jobs || 0} jobs</span>
                </div>
              </div>
              {on && <Check className="w-4 h-4 text-[#FF4D00] shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
